/**
 * The SDMS Import Files progress panel (user 2026-10-05, design "A"): a centred card over a dimmed page —
 * blocking, like the "Importing…" scrim it replaces — that lists the import's steps (done · running ·
 * waiting), ONE overall bar with its percent, and the running step's detail ("Uploading 3.2 / 8.5 MB",
 * "Calculating formulas"). Same loader, bar and colour tokens as the "Preparing the sheet" panel
 * (SdmsSheetPreparing). Upload and calculation move the bar by real progress; the steps in between move it
 * as each one finishes.
 */
import { useRef } from 'react';
import { createPortal } from 'react-dom';
import { LoaderSymbol } from '../../components/loading';

/** Upload the files · the server reads them · values into the sheet · Sequence / Raw Data refresh · recalculation. */
export type ImportStep = 'upload' | 'read' | 'write' | 'refresh' | 'calc';

export interface ImportProgress {
  step: ImportStep;
  /** The running step's OWN percent (upload by bytes, calc by formulas); null = not counted yet. */
  pct: number | null;
  files: number;
  /** Bytes sent so far / in total (upload). */
  sent: number;
  total: number;
  /** Rows the server matched to this batch — known once the read step is done. */
  rows: number | null;
  /** Calibration imports have no Sequence / Raw Data refresh. */
  hasRefresh: boolean;
}

const ORDER: readonly ImportStep[] = ['upload', 'read', 'write', 'refresh', 'calc'];

const LABEL: Record<ImportStep, string> = {
  upload: 'Upload files',
  read: 'Read files',
  write: 'Write values to the sheet',
  refresh: 'Refresh Sequence / Raw Data',
  calc: 'Recalculate formulas',
};

/** Each step's share of the overall bar, weighted by where an import spends its time (VOC, 16 files:
 *  the calculation is the longest part, then the server read). */
const SPAN: Record<ImportStep, readonly [number, number]> = {
  upload: [0, 20],
  read: [20, 40],
  write: [40, 55],
  refresh: [55, 70],
  calc: [70, 100],
};

/** The overall percent (0–100) for a step and that step's own percent. */
export function importOverallPct(step: ImportStep, pct: number | null): number {
  const [from, to] = SPAN[step];
  const within = Math.max(0, Math.min(100, pct ?? 0));
  return Math.round(from + ((to - from) * within) / 100);
}

const mb = (n: number) => (n / (1024 * 1024)).toFixed(1);

/** The detail line under the bar for the running step. */
export function importCaption(p: ImportProgress): string {
  switch (p.step) {
    case 'upload':
      return p.total > 0 ? `Uploading ${mb(p.sent)} / ${mb(p.total)} MB` : 'Uploading';
    case 'read':
      return `Reading ${p.files} file${p.files === 1 ? '' : 's'} on the server`;
    case 'write':
      return 'Writing the imported values';
    case 'refresh':
      return 'Refreshing the Sequence and Raw Data sheets';
    case 'calc':
      return p.pct == null ? 'Preparing formulas' : `Calculating formulas ${p.pct}%`;
  }
}

/** The note a finished step keeps on the right (upload: files and size; read: rows matched). */
function doneNote(step: ImportStep, p: ImportProgress): string | null {
  if (step === 'upload') return `${p.files} file${p.files === 1 ? '' : 's'}${p.total > 0 ? ` · ${mb(p.total)} MB` : ''}`;
  if (step === 'read' && p.rows != null) return `${p.rows} row${p.rows === 1 ? '' : 's'} matched`;
  return null;
}

export function SdmsImportProgress({ progress }: { progress: ImportProgress }) {
  // Only ever forward (the calculation's passes each count from 0 again), and never 100% while the panel is
  // up — it closes the moment the import is done, so "100%" would only ever be seen as a stall.
  const shownRef = useRef(0);
  const overall = Math.min(99, Math.max(shownRef.current, importOverallPct(progress.step, progress.pct)));
  shownRef.current = overall;
  const steps = ORDER.filter(s => s !== 'refresh' || progress.hasRefresh);
  const at = ORDER.indexOf(progress.step);
  return createPortal(
    <div className="sdms-imp-scrim" role="status" aria-live="polite" aria-busy="true">
      <div className="sdms-imp-card">
        <div className="sdms-imp-head">
          <span className="sdms-imp-title">Importing instrument files</span>
          <span className="sdms-imp-pct">{overall}%</span>
        </div>
        <div className="sdms-imp-bar" role="progressbar" aria-label="Importing instrument files"
          aria-valuemin={0} aria-valuemax={100} aria-valuenow={overall}>
          <span style={{ width: `${overall}%` }} />
        </div>
        <div className="sdms-imp-cap">{importCaption(progress)}</div>
        <ul className="sdms-imp-steps">
          {steps.map(s => {
            const i = ORDER.indexOf(s);
            const state = i < at ? 'done' : i === at ? 'run' : 'wait';
            const note = state === 'done' ? doneNote(s, progress) : null;
            return (
              <li key={s} className={`sdms-imp-step is-${state}`}>
                {state === 'run'
                  ? <span className="sdms-imp-ico"><LoaderSymbol size={14} /></span>
                  : <i className={`ti ${state === 'done' ? 'ti-circle-check' : 'ti-circle'}`} aria-hidden="true" />}
                <span>{LABEL[s]}</span>
                {note && <span className="sdms-imp-note">{note}</span>}
              </li>
            );
          })}
        </ul>
      </div>
    </div>,
    document.body,
  );
}
